// Quick checkout for training plans (mini-reg). Public — no account needed.
// Flow: load plan (+ waiver: per-plan waiverDocId, else the day-pass default
// from settings/membershipTiers.requiredWaiverId) -> buyer enters name/email/
// phone + accepts waiver -> createDayPassMiniAccount (dedupes returning
// buyers) -> createPlanMiniCheckoutSession -> Stripe.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../firebase';
import { Box, Typography, TextField, Button, Alert, CircularProgress, Checkbox, FormControlLabel, Link } from '@mui/material';
import { usePageMeta } from '../utils/pageMeta';

const gold = '#C9A84C';
const inputSx = {
  '& .MuiInputBase-input': { color: 'white' },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(201,168,76,0.35)' },
  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: gold },
};

export default function QuickCheckout() {
  usePageMeta('Quick Checkout | {club}', 'Buy a training plan in under a minute — no account required.');
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const planId = params.get('plan') || '';

  const [plan, setPlan] = useState(null);
  const [waiverDoc, setWaiverDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phone: '' });
  const [waiverAccepted, setWaiverAccepted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        if (!planId) { setLoadError('No plan specified.'); return; }
        const p = await getDoc(doc(db, 'plans', planId));
        if (!p.exists()) { setLoadError('Plan not found.'); return; }
        const planData = { id: p.id, ...p.data() };
        if (planData.active === false || planData.allowMiniReg !== true || planData.planType === 'recurring') {
          setLoadError('This plan is not available for quick checkout.');
          return;
        }
        if (!cancelled) setPlan(planData);

        // Waiver: per-plan, else the same default the day-pass flow uses
        let waiverId = planData.waiverDocId || '';
        if (!waiverId) {
          const settingsSnap = await getDoc(doc(db, 'settings', 'membershipTiers'));
          waiverId = settingsSnap.exists() ? (settingsSnap.data().requiredWaiverId || '') : '';
        }
        if (waiverId) {
          const w = await getDoc(doc(db, 'clubDocuments', waiverId));
          if (!cancelled && w.exists()) setWaiverDoc({ id: w.id, ...w.data() });
        }
      } catch (e) {
        console.error('quick checkout load failed:', e);
        if (!cancelled) setLoadError('Could not load this plan. Please try again.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [planId]);

  const handleSubmit = async () => {
    setError('');
    if (!form.firstName.trim() || !form.lastName.trim()) { setError('Please enter your first and last name.'); return; }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) { setError('Please enter a valid email address.'); return; }
    if (!form.phone.trim()) { setError('Please enter your phone number.'); return; }
    if (waiverDoc && !waiverAccepted) { setError('Please accept the waiver to continue.'); return; }
    if (!waiverDoc) { setError('Checkout is unavailable: no waiver is configured. Please contact us.'); return; }
    setSubmitting(true);
    try {
      const functions = getFunctions();
      const createAccount = httpsCallable(functions, 'createDayPassMiniAccount');
      const acct = await createAccount({
        email: form.email.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        phone: form.phone.trim(),
        waiverDocId: waiverDoc.id,
        waiverVersion: waiverDoc.version || null,
      });
      const userId = acct.data?.userId;
      if (!userId) throw new Error('Account step failed');

      const createCheckout = httpsCallable(functions, 'createPlanMiniCheckoutSession');
      const r = await createCheckout({
        planId: plan.id,
        userId,
        successUrl: `${window.location.origin}/payment-success`,
        cancelUrl: `${window.location.origin}/quick-checkout?plan=${plan.id}`,
      });
      if (r.data?.url) window.location.assign(r.data.url);
      else throw new Error('No checkout URL returned');
    } catch (e) {
      console.error(e);
      setError(e.message && e.message !== 'internal' ? e.message : 'Could not start checkout. Please try again.');
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#0D1E35', display: 'flex', flexDirection: 'column', alignItems: 'center', px: 2, py: 4 }}>
      <Box component="img" src="/horzlogo.png" alt="Torch Beach Volleyball"
        sx={{ maxWidth: 300, width: '75vw', cursor: 'pointer', mb: 3 }} onClick={() => navigate('/')} />

      <Box sx={{ width: '100%', maxWidth: 480 }}>
        <Typography sx={{ fontFamily: '"Bebas Neue", sans-serif', fontSize: '2rem', letterSpacing: 3, color: gold, textAlign: 'center' }}>
          Quick Checkout
        </Typography>

        {loading && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress sx={{ color: gold }} /></Box>}
        {loadError && <Alert severity="warning" sx={{ my: 3 }}>{loadError}</Alert>}

        {!loading && plan && (
          <>
            <Box sx={{ textAlign: 'center', mb: 3, p: 2, border: '1px solid rgba(201,168,76,0.35)', borderRadius: 2 }}>
              <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.05rem' }}>{plan.planName}</Typography>
              <Typography sx={{ color: gold, fontFamily: '"Bebas Neue", sans-serif', fontSize: '1.8rem' }}>
                ${plan.price}
              </Typography>
              {plan.sessionsIncluded && (
                <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.8rem' }}>
                  {plan.sessionsIncluded} sessions included
                </Typography>
              )}
            </Box>

            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', mb: 2, textAlign: 'center' }}>
              No account needed — just the basics so we know who's training.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField fullWidth label="First Name *" value={form.firstName} onChange={set('firstName')} sx={inputSx} />
                <TextField fullWidth label="Last Name *" value={form.lastName} onChange={set('lastName')} sx={inputSx} />
              </Box>
              <TextField fullWidth label="Email *" type="email" value={form.email} onChange={set('email')} sx={inputSx} />
              <TextField fullWidth label="Phone *" value={form.phone} onChange={set('phone')} sx={inputSx} />

              {waiverDoc && (
                <FormControlLabel
                  sx={{ alignItems: 'flex-start', '.MuiFormControlLabel-label': { fontSize: '0.82rem', color: 'rgba(255,255,255,0.7)' } }}
                  control={<Checkbox checked={waiverAccepted} onChange={e => setWaiverAccepted(e.target.checked)}
                    sx={{ color: gold, '&.Mui-checked': { color: gold }, pt: 0 }} />}
                  label={<span>
                    I have read and accept the{' '}
                    {/* /docs/<slug>, not /doc/<id> — the route is plural and
                        resolves by slug, so the old link 404'd. */}
                    <Link href={`/docs/${waiverDoc.slug || ''}`} target="_blank" rel="noopener" sx={{ color: gold }}>
                      {waiverDoc.name || waiverDoc.title || 'liability waiver'}
                    </Link>.
                  </span>}
                />
              )}

              <Button variant="contained" disabled={submitting} onClick={handleSubmit}
                sx={{ backgroundColor: gold, color: '#0D1E35', fontWeight: 700, py: 1.2, '&:hover': { backgroundColor: '#e0be6a' } }}>
                {submitting ? <CircularProgress size={22} sx={{ color: '#0D1E35' }} /> : `Continue to Payment — $${plan.price}`}
              </Button>
            </Box>
          </>
        )}

        <Typography sx={{ textAlign: 'center', mt: 4 }}>
          <a href="/adult-training" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem', textDecoration: 'none' }}>&larr; Back to plans</a>
        </Typography>
      </Box>
    </Box>
  );
}
